// src/components/Footer.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const LOGO_ICON_BP = 600; // ширина, ниже которой показываем компактный значок логотипа

// Модальное окно обратной связи
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--card);
  border-radius: 18px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow);
  border: 1px solid var(--bdr);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--muted);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: var(--bdr);
  }
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const FormField = styled.div`
  display: grid;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--bdr);
  background: var(--card);
  color: inherit;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(176, 141, 87, 0.25);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--bdr);
  background: var(--card);
  color: inherit;
  font-size: 16px;
  outline: none;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(176, 141, 87, 0.25);
  }
`;

const SubmitButton = styled.button`
  background: var(--primary);
  color: var(--primaryFg);
  border: none;
  padding: 14px 20px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(176, 141, 87, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

function ContactModal({ onClose }) {
  const { t } = useTranslation();
  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Имитация отправки формы
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Закрываем модальное окно через 2 секунды
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Закрытие по клику на overlay
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Закрытие по Escape
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (isSubmitted) {
    return (
      <ModalOverlay onClick={handleOverlayClick}>
        <ModalContent>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--primary)' }}>{t('contactModal.thankYou')}</h3>
            <p style={{ margin: 0, opacity: 0.8 }}>{t('contactModal.successMessage')}</p>
          </div>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{t('contactModal.title')}</ModalTitle>
          <CloseButton onClick={onClose} type="button">×</CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Label htmlFor="name">{t('contactModal.nameRequired')}</Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t('contactModal.namePlaceholder')}
            />
          </FormField>
          
          <FormField>
            <Label htmlFor="phone">{t('contactModal.phoneRequired')}</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder={t('contactModal.phonePlaceholder')}
            />
          </FormField>
          
          <FormField>
            <Label htmlFor="email">{t('contactModal.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('contactModal.emailPlaceholder')}
            />
          </FormField>
          
          <FormField>
            <Label htmlFor="message">{t('contactModal.message')}</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('contactModal.messagePlaceholder')}
            />
          </FormField>
          
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('contactModal.submitting') : t('contactModal.submit')}
          </SubmitButton>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
}


const Foot = styled.footer`
  margin-top:24px; padding:22px 18px 30px; border-top:1px solid var(--bdr);
  background: ${({theme})=>theme.glass};
`;
const Wrap = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  gap: 16px;
`;
const Grid = styled.div` 
  display: grid; 
  grid-template-columns: 1.2fr 2fr; 
  gap: 18px;
  
  @media (max-width: 900px) { 
    grid-template-columns: 1fr; 
    gap: 20px;
  }
  
  @media (max-width: 600px) {
    gap: 16px;
  }
`;
const Cols = styled.div`
  display: grid; 
  grid-template-columns: repeat(2, 1fr); 
  gap: 12px;
  
  /* Убираем переход на одну колонку на мобильных - оставляем 2 колонки */
  @media (max-width: 480px) {
    gap: 8px;
  }
`;
const Logo = styled(Link)`
  display:flex; align-items:center; flex:0 0 auto; white-space:nowrap;
`;
const LogoPicture = styled.picture`
  display: block; 
  height: 80px; /* Увеличиваем размер логотипа */
  
  img { 
    height: 80px; 
    display: block; 
    object-fit: contain; 
  }
  
  @media (max-width: ${LOGO_ICON_BP}px) {
    height: auto;
    img {
      width: 80px; /* Увеличиваем размер мобильного логотипа еще больше */
      height: auto;
    }
  }
  
  @media (max-width: 400px) {
    img {
      width: 70px; /* Увеличиваем и для маленьких экранов */
    }
  }
`;
const Socials = styled.div` display:flex; gap:8px; margin-top:10px; align-items:center; `;
const IconBtn = styled.a`
  width:40px; height:40px; border-radius:12px; border:1px solid var(--bdr); background:var(--card);
  display:flex; align-items:center; justify-content:center; box-shadow: var(--shadow);
`;
const WaBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const TgBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const ZenBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const VkBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const OkBtn = styled(IconBtn)`
  background: var(--card);
  border-color: var(--bdr);
  color: var(--fg);
  box-shadow: var(--shadow);
  svg{ width:20px; height:20px; display:block; }
`;
const ColumnLink = styled(Link)` 
  display: block; 
  text-transform: uppercase; 
  font-weight: 500; 
  letter-spacing: .3px; 
  padding: 4px 0;
  margin: 0;
  transition: opacity 0.2s ease;
  
  &:hover { 
    opacity: 0.7; 
  }
  
  /* Мелкий шрифт для политики */
  &.small {
    font-size: 12px;
    text-transform: none;
    opacity: 0.7;
    font-weight: 400;
    letter-spacing: 0;
  }
`;

const ColumnButton = styled.button`
  display: block; 
  text-transform: uppercase; 
  font-weight: 500; 
  letter-spacing: .3px; 
  padding: 4px 0;
  margin: 0;
  background: transparent !important;
  border: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  transition: opacity 0.2s ease;
  box-shadow: none !important;
  
  &:hover { 
    opacity: 0.7;
    background: transparent !important;
    transform: none !important;
    box-shadow: none !important;
  }
  
  &:focus {
    outline: none;
    background: transparent !important;
    box-shadow: none !important;
  }
  
  &:active {
    background: transparent !important;
    transform: none !important;
  }
`;

export default function Footer(){
  const { t, i18n } = useTranslation();
  const PHONE_DISPLAY = process.env.REACT_APP_PHONE_DISPLAY || '+7 (495) 972-97-97';
  const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP || '79309729797';
  const TELEGRAM_URL = process.env.REACT_APP_TELEGRAM_URL || 'https://t.me/+79309729797';
  const ZEN_URL = process.env.REACT_APP_ZEN_URL || 'https://zen.yandex.ru/astorius';
  const VK_URL = process.env.REACT_APP_VK_URL || 'https://vk.com/astorius';
  const OK_URL = process.env.REACT_APP_OK_URL || 'https://ok.ru/astorius';
  
  // Определяем текущий язык для логотипов
  const currentLang = i18n.language || 'ru';
  
  // Функция для получения правильного логотипа
  const getLogoPath = (type = 'desktop') => {
    const logoMap = {
      desktop: {
        ru: "/ASTORIUS_PREMIUP_GORIZONT_RU.svg",
        en: "/ASTORIUS_PREMIUP_GORIZONT_ENG.svg", 
        zh: "/ASTORIUS_PREMIUP_GORIZONT_CHINA.svg"
      },
      mobile: {
        ru: "/ASTORIUS_FAVICON_RU.svg",
        en: "/ASTORIUS_FAVICON_ENG.svg",
        zh: "/ASTORIUS_FAVICON_CHINA.svg"
      }
    };
    
    return logoMap[type][currentLang] || logoMap[type]['ru'];
  };
  
  // Состояние для модального окна обратной связи
  const [showContactModal, setShowContactModal] = React.useState(false);
  
  return (
    <Foot>
      <Wrap>
        <Grid>
          <div>
            <Logo to="/" aria-label="Astorius Home">
              <LogoPicture>
                {/* Мобильные логотипы (маленькие) */}
                <source 
                  media={`(max-width:${LOGO_ICON_BP}px)`} 
                  srcSet={getLogoPath('mobile')} 
                />
                {/* Десктопные логотипы (большие) */}
                <img 
                  src={getLogoPath('desktop')} 
                  alt="ASTORIUS" 
                />
              </LogoPicture>
            </Logo>
            <Socials>
              <TgBtn href={TELEGRAM_URL} target="_blank" rel="noreferrer" title="Telegram" aria-label="Telegram">
                <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" />
                  <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </TgBtn>
              <ZenBtn href={ZEN_URL} target="_blank" rel="noreferrer" title="Яндекс Дзен" aria-label="Яндекс Дзен">
                <svg viewBox="0 0 28 28" aria-hidden="true">
                  <path d="M16.7 16.7c-2.2 2.27-2.36 5.1-2.55 11.3 5.78 0 9.77-.02 11.83-2.02 2-2.06 2.02-6.24 2.02-11.83-6.2.2-9.03.35-11.3 2.55M0 14.15c0 5.59.02 9.77 2.02 11.83 2.06 2 6.05 2.02 11.83 2.02-.2-6.2-.35-9.03-2.55-11.3-2.27-2.2-5.1-2.36-11.3-2.55M13.85 0C8.08 0 4.08.02 2.02 2.02.02 4.08 0 8.26 0 13.85c6.2-.2 9.03-.35 11.3-2.55 2.2-2.27 2.36-5.1 2.55-11.3m2.85 11.3C14.5 9.03 14.34 6.2 14.15 0c5.78 0 9.77.02 11.83 2.02 2 2.06 2.02 6.24 2.02 11.83-6.2-.2-9.03-.35-11.3-2.55" fill="#2C3036"/>
                  <path fill="currentColor" d="M28 14.15v-.3c-6.2-.2-9.03-.35-11.3-2.55-2.2-2.27-2.36-5.1-2.55-11.3h-.3c-.2 6.2-.35 9.03-2.55 11.3-2.27 2.2-5.1 2.36-11.3 2.55v.3c6.2.2 9.03.35 11.3 2.55 2.2 2.27 2.36 5.1 2.55 11.3h.3c.2-6.2.35-9.03 2.55-11.3 2.27-2.2 5.1-2.36 11.3-2.55" style={{fill: 'transparent'}}/>
                </svg>
              </ZenBtn>
              <VkBtn href={VK_URL} target="_blank" rel="noreferrer" title="ВКонтакте" aria-label="ВКонтакте">
                <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                  <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1.01-1.49-.9-1.744-.9-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4.03 8.57 4.03 8.096c0-.254.102-.491.593-.491h1.744c.441 0 .61.203.78.678.863 2.49 2.303 4.675 2.896 4.675.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.204.169-.407.441-.407h2.744c.373 0 .508.203.508.643v3.473c0 .372.169.508.271.508.22 0 .407-.136.813-.542 1.254-1.406 2.151-3.574 2.151-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.271.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
                </svg>
              </VkBtn>
              <OkBtn href={OK_URL} target="_blank" rel="noreferrer" title="Одноклассники" aria-label="Одноклассники">
                <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.8c1.988 0 3.6 1.612 3.6 3.6S13.988 12 12 12s-3.6-1.612-3.6-3.6S10.012 4.8 12 4.8zm0 2.4c-.663 0-1.2.537-1.2 1.2s.537 1.2 1.2 1.2 1.2-.537 1.2-1.2-.537-1.2-1.2-1.2zm5.4 7.2c0 .663-.537 1.2-1.2 1.2h-1.8l2.4 2.4c.469.469.469 1.231 0 1.7-.234.234-.541.35-.85.35s-.616-.116-.85-.35L12 16.6l-3.1 3.1c-.234.234-.541.35-.85.35s-.616-.116-.85-.35c-.469-.469-.469-1.231 0-1.7l2.4-2.4H7.8c-.663 0-1.2-.537-1.2-1.2s.537-1.2 1.2-1.2h8.4c.663 0 1.2.537 1.2 1.2z"/>
                </svg>
              </OkBtn>
            </Socials>
          </div>
          <Cols>
            <div>
              <ColumnLink to="/quiz">{t('footer.pickObject')}</ColumnLink>
              <ColumnButton onClick={() => setShowContactModal(true)}>{t('footer.sellProperty')}</ColumnButton>
              <ColumnLink to="/map">{t('footer.map')}</ColumnLink>
              <ColumnLink to="/search">{t('footer.newbuilds')}</ColumnLink>
              <ColumnLink to="/secondary">{t('footer.secondary')}</ColumnLink>
              <ColumnLink to="/rent">{t('footer.rent')}</ColumnLink>
            </div>
            <div>
              <ColumnLink to="/contacts">{t('footer.contacts')}</ColumnLink>
              <ColumnLink to="/about">{t('footer.about')}</ColumnLink>
              <ColumnLink to="/socialmedia">{t('footer.media')}</ColumnLink>
              <ColumnLink to="/blog">{t('footer.blog')}</ColumnLink>
              <ColumnLink to="/vacancies">{t('footer.vacancies')}</ColumnLink>
            </div>
          </Cols>
        </Grid>
        
        {/* Политика отдельно мелким шрифтом */}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--bdr)' }}>
          <ColumnLink to="/privacy" className="small">{t('footer.privacy')}</ColumnLink>
        </div>
        
        <div style={{opacity:.6, marginTop:6}}>{t('footer.rights')}</div>
      </Wrap>
      
      {/* Модальное окно обратной связи */}
      {showContactModal && (
        <ContactModal onClose={() => setShowContactModal(false)} />
      )}
    </Foot>
  );
}
